/**
 * Ficha de una oferta.
 *
 * Es una pantalla, no un panel: vive en su propia dirección (`&record=<id>`),
 * igual que un contacto o un mensaje, de modo que "atrás" vuelve a la lista y
 * un enlace a una oferta concreta se puede pasar a alguien.
 *
 * ── Se lee aquí, se cambia en el asistente ───────────────────────────────────
 * Esto fue un formulario en cinco pestañas —Contenido, Archivos, Precios,
 * Entrega y Ajustes— que preguntaba, campo por campo, exactamente lo mismo que
 * el asistente del alta. Dos formularios sobre los mismos datos es el camino
 * más corto a que digan cosas distintas: había que mantener dos veces cada
 * validación, y la que faltaba era la de aquí —esta ficha dejaba guardar una
 * oferta sin importe que el asistente no deja crear—.
 *
 * Así que aquí no se escribe. Cada dato lleva su atajo al paso del asistente
 * donde se cambia: el resumen y el índice de edición son la misma lista. Lo
 * único inmediato son las acciones de un clic —la portada y publicar—, que no
 * necesitan borrador: lo que se pulsa es lo que pasa.
 *
 * ── La planta ────────────────────────────────────────────────────────────────
 * Barra de sección, pestañas, y debajo dos columnas: a la izquierda la pestaña
 * abierta —lo vendido, las analíticas, su página o sus ajustes— y a la derecha,
 * fija y a la vista desde todas, la tarjeta de la oferta: portada, nombre,
 * estado, descripción, los tres datos de pasada y los mandos del enlace.
 *
 * La misma que la ficha de un servicio con agenda, y a propósito la misma: son
 * la misma clase de cosa y no tienen por qué mirarse distinto.
 */
import { useEffect, useRef, useState } from 'react'
import { offerShortDescription } from '@webbuilder/schemas'
import { useTabThumb } from '../../../hooks/useTabThumb'
import { DashboardPageHeader } from '../../DashboardPageHeader'
import { useUnsavedGuard } from '../../../lib/unsavedChanges'
import { ConfirmDialog } from '../ConfirmDialog'
import { ImageLibraryPickerDialog } from '../../ImageLibraryPickerDialog'
import { Notice } from '../Notice'
import { NewOfferWizard, type OfferWizardStep } from './NewOfferWizard'
import { MembershipEditor } from './MembershipsSection'
import { FreeMembersList } from './FreeMembersList'
import { OfferCard } from './OfferCard'
import { OfferSalesNotice } from './OfferSalesNotice'
import { OfferPageSettings } from './OfferPageSettings'
import { OfferAppearanceSettings } from './OfferAppearanceSettings'

/** Ver la pestaña «Apariencia», más abajo: escondida mientras se prueba la plantilla del editor. */
const HIDE_LOOK_TAB = true
import type { ProjectTheme } from '../../../lib/preview/types'
import { OfferStatusSelect, PublishBlockDialog } from './OfferStatusSelect'
import { PlanLimitModal } from '../PlanLimitModal'
import { OrdersTable } from './OrdersTable'
import { OfferAnalytics } from './OfferAnalytics'
import { useToast } from '../../Toast'
import {
  archiveOffer,
  listOffers,
  listOrders,
  updateOffer,
  type ConnectStatus,
  type Offer,
  type Order,
} from '../../../lib/api'
import { cadenceKeyOf, failureDetail, isFree, publishBlockFor, THANK_YOU_KEY, TYPE_KEY, mainPrice, type PublishBlock } from './offerShared'
import { useRegion } from '../../../lib/region'
import { translateServerError, useI18n } from '../../../lib/i18n'
import type { BillingPlan } from '../../../lib/api'
import {
  IconAlignLeft,
  IconArrowLeft,
  IconBell,
  IconCalendarEvent,
  IconCalendarPlus,
  IconCheck,
  IconChartBar,
  IconCircleCheck,
  IconClipboard,
  IconCopy,
  IconCreditCard,
  IconCurrencyDollar,
  IconExternalLink,
  IconEye,
  IconFiles,
  IconFingerprint,
  IconHistory,
  IconLoader2,
  IconPalette,
  IconPencil,
  IconPhotoPlus,
  IconSettings,
  IconShoppingCart,
  IconTag,
  IconTruckDelivery,
  IconStack2,
  IconListDetails,
  IconWorld,
  IconX,
} from '@tabler/icons-react'
import { offerTemplateKindOf, useOfferFields } from './OfferCustomFields'
import { OfferTagsCard } from './OfferTagsCard'
import type { KnownOption } from './BlogPickerField'
import { RecordPhotoStrip } from './RecordPhotoStrip'
import { VariantStockCard } from './VariantStockCard'

export function OfferDetail({ projectId, token, offer, connectStatus, canConnectStripe, plan, publishedOffers, brands, onBrandRemoved, tags, onTagRemoved, onBack, onChange, onArchived, onOpenStripeSettings, onOpenPlans, onOpenBookingService, siteOrigin, onOpenPublish, onOpenContact, theme }: {
  projectId: string
  token: string
  offer: Offer
  /** `null` mientras no se sabe: al llegar por enlace la cuenta aún viaja, y
   *  dar por hecho que no hay ninguna hacía parpadear el aviso a quien sí la
   *  tiene conectada. */
  connectStatus: ConnectStatus | null
  /**
   * Si el plan permite darse de alta en Stripe.
   *
   * Cambia a dónde lleva el aviso de publicar: sin membresía no hay alta que
   * empezar, así que mandarlo a Stripe sería mandarlo a topar con otra reja.
   */
  canConnectStripe: boolean
  /** De paso al asistente, que es quien decide si «a la venta» cabe. */
  plan: BillingPlan
  publishedOffers: number
  /** Las marcas del proyecto (con cuántas ofertas), para elegir la de ésta. */
  brands?: KnownOption[]
  onBrandRemoved?: (name: string) => void
  /** Las etiquetas del proyecto (con cuántas ofertas). */
  tags?: KnownOption[]
  onTagRemoved?: (name: string) => void
  onBack: () => void
  /** La oferta que devuelve el servidor tras cada cambio; la lista vive fuera. */
  onChange: (offer: Offer) => void
  onArchived: () => void
  /** Publicar sin cuenta no se puede: de aquí sale el camino a arreglarlo. */
  onOpenStripeSettings: () => void
  /** Y el camino de antes, para quien todavía no puede ni conectar. */
  onOpenPlans: () => void
  /**
   * Salida a la ficha de Reservas, cuando esta oferta es un servicio reservable.
   *
   * A esta pantalla ya no se llega pulsando una reserva en el catálogo —esas
   * filas van derechas a su ficha—, pero sí por un enlace guardado o por el
   * historial. Entonces hay que decir dónde se edita en vez de dejar dos
   * formularios abiertos sobre la misma fila.
   */
  onOpenBookingService?: ((offerId: string) => void) | undefined
  /** El dominio público del sitio, o `null` si todavía no se ha publicado. */
  siteOrigin?: string | null | undefined
  /**
   * Abre la ficha del comprador en Contactos, desde la tabla de ventas.
   *
   * El mismo camino que ofrece Cobros, porque es la misma tabla. Sin él las
   * filas siguen abriéndose —la venta se lee igual—, sólo que el nombre de quien
   * compró deja de llevar a ninguna parte.
   */
  onOpenContact?: ((identity: string) => void) | undefined
  /** Y el editor señalando Publicar, para el sitio que aún no lo está. */
  onOpenPublish?: (() => void) | undefined
  /** El tema del sitio: los tokens que ofrece la pestaña «Apariencia». */
  theme?: ProjectTheme | undefined
}) {
  // La que llevaría un precio: se enseña donde todavía no hay ninguno.
  const { currency: projectCurrency } = useRegion()
  const { showToast } = useToast()
  const { t, fmt } = useI18n()
  /** Qué campo está esperando al servidor. Sólo uno a la vez, y se dice. */
  const [savingField, setSavingField] = useState('')
  /*
   * Las cuatro pestañas, y por qué ahora sí.
   *
   * Aquí hubo pestañas y se quitaron con un motivo escrito: dos de las cuatro
   * estaban vacías y no se podía ver a la vez el precio y lo que se vende. Las
   * dos objeciones siguen siendo buenas, así que ésta es otra planta y no la
   * misma otra vez — la de la ficha de un servicio reservable, que resuelve las
   * dos:
   *
   *   • La tarjeta de la derecha NO es una pestaña: está siempre, así que el
   *     precio, el estado y la portada se ven mientras se edita cualquier cosa.
   *   • Y ninguna de las cuatro está vacía. Lo que era la pestaña «Checkout»
   *     —una lista de lo que vendrá— vive dentro de Entrega, junto a lo que sí
   *     se puede tocar hoy.
   */
  /**
   * Las dos pestañas, y por qué sólo dos.
   *
   * Aquí hubo cinco y eran un formulario partido en trozos; se fueron al
   * asistente. Éstas son de otra clase: no reparten campos, reparten dos cosas
   * que no se miran a la vez —lo que la oferta ha vendido y lo que la oferta
   * es—. Ventas primero, que es a lo que se entra: la ficha se abre para ver
   * cómo va, no para releer su identificador.
   */
  /* Lo que queda de un producto físico: con variantes, todas sumadas (lo
     calcula el servidor); sin ellas, las del producto. */
  const stockLeft = offer.availableStock !== undefined ? offer.availableStock : offer.stock
  const [tab, setTab] = useState<'ventas' | 'analiticas' | 'avisos' | 'pagina' | 'apariencia' | 'ajustes'>('ventas')
  const tabsRef = useRef<HTMLDivElement | null>(null)

  /**
   * Lo vendido por ESTA oferta.
   *
   * Se piden todos los cobros del proyecto y se queda con los suyos, que es lo
   * mismo que hace Cobros: el servidor no filtra por oferta y montar esa ruta
   * para una lista que ya viaja entera no cambiaría nada de lo que se ve. Si
   * algún día pesa, el filtro es de una línea aquí y de un parámetro allí.
   */
  const [orders, setOrders] = useState<Order[] | null>(null)
  const [ordersFailed, setOrdersFailed] = useState(false)
  const [ordersFailure, setOrdersFailure] = useState('')
  const [ordersReload, setOrdersReload] = useState(0)

  /*
   * Un servicio reservable es una oferta, pero no se edita aquí.
   *
   * Su precio lo pone el paso 3 del asistente de Reservas, que además sabe
   * retirar el viejo al poner el nuevo en el orden que el servidor acepta. Con
   * las dos pantallas abiertas, la única pregunta que importa —¿cuál manda?—
   * no tiene respuesta, así que Precios no sale y arriba se dice dónde ir.
   *
   * Lo demás se queda: la descripción, las imágenes y sobre todo el destino
   * después de pagar, que el asistente de Reservas no toca.
   */
  const isBookable = offer.booking.durationMinutes !== null
  /* Los campos personalizados de su tipo, para enseñarlos en el resumen. */
  const { fields: offerFields } = useOfferFields(projectId, token)
  const customRows = (offerFields?.[offerTemplateKindOf({ type: offer.type, bookingDurationMinutes: offer.booking.durationMinutes })] ?? []).map((field) => {
    const value = offer.customValues?.[field.id] ?? ''
    const shown = !value ? t('offerFields.unset')
      : field.type === 'image' ? t('offerFields.imageSet')
        : value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
    return [field.label, shown, 'extra', 'custom', IconListDetails] as const
  })

  /**
   * El asistente abierto, y por qué paso.
   *
   * `null` es cerrado. Se guarda el paso y no un booleano porque cada recuadro
   * del resumen abre por el suyo: quien pulsa «Precio» quiere el paso del
   * precio, no el principio de un formulario de cinco.
   */
  /* «Por completar» abre directo su asistente: la ficha de algo que la IA
     dejó a medias no tiene nada que leer todavía. */
  const [editStep, setEditStep] = useState<OfferWizardStep | null>(offer.setupPending ? 'info' : null)
  /*
   * Los archivos son de lo digital y de nada más.
   *
   * Una reserva se entrega en persona y un curso tendrá sus lecciones; poner
   * aquí una pestaña de archivos para los tres sería ofrecer una entrega que
   * dos de ellos no hacen. Sigue al TIPO y no a si ya hay archivos: la pestaña
   * tiene que existir antes del primero, que es cuando hace falta.
   *
   * Y al tipo GUARDADO, no al del borrador: subir un archivo escribe en el
   * servidor de inmediato, así que abrir la pestaña por un cambio de tipo sin
   * guardar dejaría archivos colgando de una oferta que, si se descarta el
   * borrador, vuelve a no ser digital.
   */
  const isDigital = !isBookable && offer.type === 'digital'
  const isService = !isDigital
  /*
   * El enlace de la oferta.
   *
   * Toda oferta tiene ahora su dirección, y son dos puertas distintas: la
   * gratuita lleva a su formulario de reclamación —el recurso a cambio del
   * correo— y la de pago a su página de compra, que sirve el worker. Ver
   * `routes/freeClaim.ts` y `routes/offerPage.ts`.
   *
   * Antes sólo la gratuita tenía: la de pago se vendía —y se sigue vendiendo—
   * con su botón dentro de una página tuya, así que no había nada que copiar
   * para mandarlo por WhatsApp o pegarlo en una historia. El botón sigue siendo
   * la forma de venderlo DENTRO del sitio; esto es la que existe siempre.
   *
   * Sin sitio publicado no hay dominio donde vivir, y entonces no hay enlace que
   * enseñar todavía. Una reserva tampoco entra aquí: la suya es su página de
   * reserva y se gestiona en su sección.
   */
  const isFreeOffer = isFree(offer)
  /* Con la dirección y no con el identificador: las dos rutas aceptan las dos
     —ver `findOffer` en el worker— y la que se comparte tiene que poder leerse.
     Los botones de compra puestos en las páginas siguen llevando el id dentro,
     que es lo que no cambia nunca. */
  const publicPrefix = isFreeOffer ? '/checkout/free/' : '/checkout/buy/'
  const publicUrl = siteOrigin && !isBookable ? `${siteOrigin}${publicPrefix}${offer.slug}` : null
  const live = offer.status === 'active'

  /** Un segundo de «copiado» en el propio botón. Ver el comentario de la barra. */
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(false), 1600)
    return () => window.clearTimeout(timer)
  }, [copied])

  const copyLink = async () => {
    if (!publicUrl) return
    try {
      await navigator.clipboard.writeText(publicUrl)
      setCopied(true)
      showToast(t('common.linkCopied'))
    } catch {
      showToast(t('common.copyFailed'), 'error')
    }
  }
  const [archiveOpen, setArchiveOpen] = useState(false)
  /* El motivo entero y no su nombre: el del cupo lleva el plan encima, que es
     lo que su ventana necesita para ofrecer el siguiente. */
  const [explainingPublish, setExplainingPublish] = useState<PublishBlock>(null)
  const [imagePickerOpen, setImagePickerOpen] = useState(false)

  /*
   * Sin borrador ni «Guardar»: en esta pantalla ya no se escribe.
   *
   * El nombre y la descripción se editaban aquí mismo, en la tarjeta de la
   * derecha, y por eso hacía falta todo el aparato —lo escrito, lo guardado, lo
   * que cambió, la barra de arriba y el aviso al salir—. Esa tarjeta se fue a
   * Ajustes y allí cada dato abre el asistente, que guarda por su cuenta. Lo que
   * queda inmediato son dos acciones de un clic, la portada y publicar, y una
   * acción de un clic no necesita borrador: lo que se pulsa es lo que pasa.
   */
  /* Salir sigue preguntando por si otra pantalla del panel tiene algo a medias
     —el registro es común— y es lo que usan los saltos a Reservas y a Publicar. */
  const blockUnsaved = useUnsavedGuard()

  const guardedBack = () => {
    if (blockUnsaved()) return
    onBack()
  }

  /* Al entrar en la ficha, y no al abrir la pestaña: Ventas es la que se abre
     sola, así que esperar a que alguien la pulse sería esperar a nada. Y las
     usan las dos primeras pestañas —la tabla y las analíticas—, que salen de
     esta misma lista. Cambiar de oferta vuelve a pedirlas. */
  useEffect(() => {
    let alive = true
    setOrders(null)
    listOrders(projectId, token)
      .then((rows) => {
        if (!alive) return
        setOrders(rows.filter((order) => order.offerId === offer.id))
        setOrdersFailed(false)
        setOrdersFailure('')
      })
      .catch((error: unknown) => {
        console.error('[oferta] no se pudieron cargar sus ventas', error)
        if (!alive) return
        setOrders([])
        setOrdersFailure(failureDetail(error))
        setOrdersFailed(true)
      })
    return () => { alive = false }
  }, [projectId, token, offer.id, ordersReload])

  /* La pastilla que se desliza, medida y no repartida: «Ventas» y «Detalles» no
     miden lo mismo. El mismo gesto que la ficha de un servicio reservable. */
  useTabThumb(tabsRef, tab)

  /**
   * Abre el asistente por un paso, salvo que haya algo sin guardar.
   *
   * El nombre se edita aquí mismo, en el título de la tarjeta, y el asistente
   * carga lo GUARDADO: entrar con un nombre a medio escribir y darle a guardar
   * allí lo devolvería al que tenía sin que nadie lo haya pedido. Con cambios
   * pendientes parpadea la barra de arriba, que es donde está la solución.
   */
  /* Una membresía se edita con SU asistente (precio, qué clases incluye y el
     descuento de miembro), no con el de las demás ofertas. */
  const [membershipOpen, setMembershipOpen] = useState(false)
  /* Membresía (cobro recurrente) o paquete de clases (pago único). */
  const membershipKind = offer.membership
    ? (offer.prices.some((row) => row.active && row.interval !== 'one_time') ? 'membership' : 'pack')
    : null
  /* Gratis: membresía sin ningún precio mayor que cero. */
  const freeMembership = membershipKind === 'membership' && !offer.prices.some((row) => row.active && row.unitAmount > 0)
  const openWizard = (step: OfferWizardStep) => {
    if (blockUnsaved()) return
    if (offer.membership) { setMembershipOpen(true); return }
    setEditStep(step)
  }
  const reloadAfterMembership = async () => {
    setMembershipOpen(false)
    const fresh = (await listOffers(projectId, token).catch(() => [])).find((row) => row.id === offer.id)
    if (fresh) onChange(fresh)
  }

  /** Lo que hay que enseñar y lo que se manda: el cuerpo que acepta el servidor. */
  const patch = async (body: Parameters<typeof updateOffer>[2], field: string) => {
    setSavingField(field)
    try {
      onChange(await updateOffer(projectId, offer.id, body, token))
    } catch (error) {
      showToast(translateServerError(error, t('error.unexpected')), 'error')
    } finally {
      setSavingField('')
    }
  }

  const price = mainPrice(offer)
  /* `fmt.date` en vez del `Intl` de módulo que había aquí: escribía «29 de
     agosto de 2026» aunque el panel estuviera en inglés, y además ya devuelve
     el guion cuando no hay fecha. */
  const created = fmt.date(offer.createdAt)
  const updated = fmt.date(offer.updatedAt)

  /**
   * Qué impide publicarla, si es que algo lo impide.
   *
   * Publicar es lo único de esta pantalla que necesita a Stripe: todo lo demás
   * —nombre, imágenes, precio— se guarda sin cuenta conectada. Y despublicar
   * nunca se bloquea: retirar algo de la venta no puede depender de nada.
   *
   * Lo calcula la misma regla que usan la lista y Reservas, y lo cuenta el mismo
   * selector: sin cuenta se puede conectar, y sin plan lo que falta es la
   * membresía —ofrecer «Conectar Stripe» ahí sería mandar a una puerta cerrada—.
   * El servidor lo comprueba igual (`assertCanPublish`): esto es la explicación
   * y el camino, no la reja.
   */
  const publishBlock = publishBlockFor(offer, {
    /* `null` mientras no se sabe: un fallo de red nuestro no puede impedirle
       publicar a quien sí puede. Ver `publishBlockFor`. */
    stripeReady: connectStatus === null ? null : connectStatus === 'ready',
    canConnectStripe,
    plan,
    publishedOffers,
  })

  /* Sólo se copia el identificador, así que el aviso es uno y va entero en el
     catálogo: componerlo como «{cosa} copiado» obliga a que el género del
     participio cuadre con una palabra que el traductor elige aparte. */
  const copyId = () => {
    void navigator.clipboard?.writeText(offer.id)
    showToast(t('offers.detail.idCopied'))
  }

    const confirmArchive = async () => {
    try {
      await archiveOffer(projectId, offer.id, token)
      setArchiveOpen(false)
      showToast(t('offers.withdrawn'))
      onArchived()
    } catch (error) {
      showToast(translateServerError(error, t('error.unexpected')), 'error')
    }
  }

  return (
    <div className="dash-projects-view dash-pages-view dash-offer-detail-view">
      {/*
        La barra de sección, con la salida a la derecha.

        Decía «Oferta» sobre «Ofertas» —el mismo nombre dos veces, ninguna de las
        dos nombrando a ÉSTA— y se quitó entera; con ella se fue el botón de
        volver a su renglón, donde se estiraba a lo ancho de la pantalla.

        Dice QUÉ estás mirando: el nombre de la oferta arriba y su categoría
        debajo. Antes decía el catálogo y el tipo —«All offers» sobre
        «Booking»—, y eso nombraba la lista de la que venías, no la cosa que
        tenías delante; con tres fichas abiertas seguidas la barra era idéntica
        en las tres. De dónde vienes lo sigue diciendo el botón de volver, que
        es donde se pulsa para ir.
      */}
      <DashboardPageHeader
        title={offer.title || t('common.untitled')}
        subtitle={membershipKind ? t(`memberships.kind.${membershipKind}`) : isBookable ? t('offers.kind.booking') : t(TYPE_KEY[offer.type])}
        actions={(
          <button className="dash-secondary-btn dash-detail-back" type="button" onClick={guardedBack}>
            <IconArrowLeft size={14} /> {t(membershipKind ? 'memberships.detail.back' : 'offers.detail.back')}
          </button>
        )}
      />

      {/* Sin barra de «Guardar» arriba: esta pantalla ya no tiene campos que
          escribir. Lo que se cambia aquí —la portada y publicar— se aplica al
          pulsarlo, y el resto abre el asistente, que trae la suya. */}

      {/* Aquí iba el aviso de Stripe —y, sin plan que lo permita, la oferta de
          membresía—. Se va de la ficha: es del sitio entero, ya encabeza la
          lista de ofertas y aquí no bloquea nada que se venga a hacer. Lo que sí
          hace falta se dice en el momento exacto: al intentar publicar, el
          diálogo de Ajustes cuenta cuál de las dos cosas falta y lleva a
          arreglarla. */}

      {/* Antes que nada: si esto se atiende en otra sección, decirlo antes de
          que alguien empiece a escribir en la pestaña equivocada. */}
      {isBookable ? (
        <Notice
          tone="brand"
          icon={<IconCalendarEvent size={17} />}
          title={t('offers.detail.bookable.title')}
          {...(onOpenBookingService ? {
            actions: (
              <button type="button" className="dash-secondary-btn" onClick={() => { if (!blockUnsaved()) onOpenBookingService(offer.id) }}>
                <IconCalendarEvent size={14} /> {t('offers.detail.bookable.open')}
              </button>
            ),
          } : {})}
        >
          <p>{t('offers.detail.bookable.body')}</p>
        </Notice>
      ) : null}

      {/* Las pestañas van FUERA de las dos columnas: son de la ficha entera, no
          de la columna izquierda. Dentro de ella, la tarjeta de la derecha
          arrancaría a su misma altura y quedaría un renglón por encima del
          contenido; así las dos columnas empiezan donde acaban las pestañas. */}
      <div
        className="dash-booking-service-tabs dash-tabs-icon-collapse"
        ref={tabsRef}
        role="tablist"
        aria-label={t('offers.detail.tabsAria')}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
          const tabs = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'))
          const current = tabs.indexOf(document.activeElement as HTMLButtonElement)
          if (current < 0) return
          event.preventDefault()
          const direction = event.key === 'ArrowRight' ? 1 : -1
          const next = tabs[(current + direction + tabs.length) % tabs.length]
          next?.focus()
          next?.click()
        }}
      >
        {/* La pastilla que se desliza va antes que los botones en el DOM y por
            debajo con z-index, para que el texto de la activa se lea encima. */}
        <span className="dash-booking-service-tabs-thumb" aria-hidden="true" />
        {([
          ['ventas', 'offers.detail.tab.sales', IconShoppingCart],
          /* Los números de ESTA oferta, no los del catálogo: la sección de
             Analíticas de venta mide el proyecto entero y aquí la pregunta es
             cómo va esto. Se calcula con los mismos cobros que llenan la tabla
             de al lado, así que las dos pestañas no pueden contradecirse. */
          ['analiticas', 'offers.detail.tab.analytics', IconChartBar],
          ['avisos', 'offers.detail.tab.notices', IconBell],
          /* La PÁGINA de la oferta, que no es la oferta: cómo se titula en un
             buscador, qué se ve al pegar el enlace y quién la encuentra. Está
             aquí y no en el editor porque una oferta no se abre en el editor
             —su página la sirve el worker— y porque se crea, se cobra y se mide
             en esta pantalla. */
          ['pagina', 'offers.detail.tab.page', IconWorld],
          /* Cómo se VE esa página: colores, textos y botón, elegidos entre los
             tokens del sitio. Aparte de «Página» porque se decide mirando —con
             la vista previa al lado— y aquélla se decide escribiendo. Una
             oferta con agenda no la tiene: su página es la de reservar, que se
             lleva desde Reservas. */
          /* ESCONDIDA a prueba (2026-10-05): el aspecto de la página de una
             oferta pasa a diseñarse en el editor, como plantilla por tipo
             («Páginas → Productos → Página de cada producto»). Si convence, se
             borra esta pestaña con `OfferAppearanceSettings` y `page_style`;
             si no, se vuelve a encender quitando `HIDE_LOOK_TAB`. */
          ...(isBookable || HIDE_LOOK_TAB ? [] : [['apariencia', 'offers.detail.tab.look', IconPalette] as const]),
          /* «Ajustes» y no «Detalles»: dentro no hay una ficha técnica, está
             TODO lo que define la oferta —su nombre, su precio, lo que entrega,
             si se ve— con su atajo al paso del asistente que lo cambia. */
          ['ajustes', 'offers.detail.tab.settings', IconSettings],
        ] as const).map(([value, labelKey, Icon]) => (
          <button
            key={value}
            id={`offer-tab-${value}`}
            type="button"
            role="tab"
            aria-label={t(labelKey)}
            aria-selected={tab === value}
            aria-controls={`offer-panel-${value}`}
            tabIndex={tab === value ? 0 : -1}
            className={tab === value ? 'is-active' : ''}
            /* Cambiar de pestaña desmonta la que se deja, así que con algo sin
               guardar dentro —los ajustes de página se escriben y se guardan al
               final— saldría por un clic y sin avisar. El mismo guardián que
               usa «Atrás»: parpadea la barra de arriba, que es donde está la
               solución, y la pestaña no se mueve. */
            onClick={() => { if (!blockUnsaved()) setTab(value) }}
          >
            <Icon size={15} />
            {/* Sin la cuenta de ventas al lado: una pestaña dice a dónde se va,
                y el número ya está en el resumen de la derecha, que se ve desde
                las dos. */}
            <span>{t(labelKey)}</span>
          </button>
        ))}
      </div>

      {/* En «Apariencia» sin la tarjeta de la derecha: ahí se mira una página
          entera, y trescientos píxeles menos eran una vista previa más pequeña
          para enseñar una tarjeta que nada tiene que ver con cómo se ve. */}
      <div className={`dash-booking-service-layout${tab === 'apariencia' ? ' is-single' : ''}`}>
        {/*
          A la izquierda, las dos pestañas; a la derecha, la oferta.

          Y en ninguna de las dos hay un campo que el asistente pregunte. Aquí
          hubo cinco pestañas —Contenido, Archivos, Precios, Entrega y Ajustes—
          que repetían, campo por campo, lo que ya pregunta él: el nombre, la
          descripción, las imágenes, los archivos, el importe y el destino
          después de pagar. Dos formularios sobre los mismos datos es el camino
          más corto a que digan cosas distintas, y obligaba a mantener dos veces
          cada validación —la de aquí acabó siendo la que faltaba: la ficha
          dejaba guardar una oferta sin importe que el asistente no deja crear—.

          Se edita en el asistente, al que llevan los atajos de la tarjeta de la
          derecha. Es el mismo camino que tomó la ficha de un servicio
          reservable, y por eso las dos se parecen tanto: son la misma pantalla.
        */}
        <main
          id={`offer-panel-${tab}`}
          /* Las dos pestañas de tarjetas comparten los ajustes finos de las
             tarjetas —el filo redondo, el aire en pantalla estrecha—: son la
             misma tarjeta y no pueden medir distinto según la pestaña. */
          className={`dash-booking-service-main${tab === 'ajustes' || tab === 'pagina' || tab === 'avisos' ? ' dash-commerce-details' : ''}`}
          role="tabpanel"
          aria-labelledby={`offer-tab-${tab}`}
        >
          {/* Sin el nombre de la pestaña repetido aquí dentro: lo dice la
              pastilla de arriba, que está a la vista, y debajo iba además una
              frase explicando lo que la propia tabla ya enseña. Dos renglones
              de introducción antes de cada pestaña son dos renglones que se
              saltan siempre y que retrasan lo que se viene a mirar. */}
          {tab === 'avisos' ? (
            <OfferSalesNotice
              projectId={projectId}
              token={token}
              offer={offer}
              onChange={onChange}
              bookable={isBookable}
            />
          ) : tab === 'pagina' ? (
            <OfferPageSettings
              projectId={projectId}
              token={token}
              offer={offer}
              siteOrigin={siteOrigin}
              onChange={onChange}
            />
          ) : tab === 'apariencia' ? (
            <OfferAppearanceSettings
              projectId={projectId}
              token={token}
              offer={offer}
              theme={theme}
              siteOrigin={siteOrigin}
              onChange={onChange}
            />
          ) : tab === 'analiticas' ? (
            <OfferAnalytics
              orders={orders ?? []}
              loading={orders === null}
              currency={price?.currency ?? projectCurrency}
              free={isFreeOffer}
            />
          ) : tab === 'ventas' ? (
            /* Lo vendido, con la misma tabla que Cobros: las mismas columnas,
               el mismo buscador y la misma ficha al abrir una fila. Aquí sin la
               columna de la oferta —todas son suyas— y sin su filtro. */
            <>
              {offer.status === 'draft' && orders !== null && orders.length === 0 ? (
                <Notice
                  tone="accent"
                  title={
                    isBookable
                      ? t('bookings.svc.detail.draftNotice.title')
                      : membershipKind
                        ? t(`memberships.detail.draftTitle.${membershipKind}`)
                      : isService
                        ? t('offers.detail.draftNotice.serviceTitle')
                        : t('offers.detail.draftNotice.title')
                  }
                  actions={
                    <button
                      type="button"
                      className="dash-primary-btn"
                      disabled={savingField === 'status'}
                      onClick={() => {
                        if (publishBlock !== null) {
                          setExplainingPublish(publishBlock)
                        } else {
                          void patch({ status: 'active' }, 'status')
                        }
                      }}
                    >
                      {savingField === 'status' ? <IconLoader2 size={15} className="dash-spin" /> : null}
                      {t('offers.detail.draftNotice.publish')}
                    </button>
                  }
                >
                  <p>
                    {isBookable
                      ? t('bookings.svc.detail.draftNotice.body')
                      : membershipKind
                        ? t('memberships.detail.draftBody')
                      : isService
                        ? t('offers.detail.draftNotice.serviceBody')
                        : t('offers.detail.draftNotice.body')}
                  </p>
                </Notice>
              ) : null}

              {/* Una membresía gratis no vende: se une gente. Sus «pedidos de 0»
                  se leen como miembros. */}
              {freeMembership ? (
                <FreeMembersList orders={orders ?? []} loading={orders === null} {...(onOpenContact ? { onOpenContact } : {})} />
              ) : (
              <OrdersTable
                projectId={projectId}
                token={token}
                orders={orders ?? []}
                loading={orders === null}
                failed={ordersFailed}
                failureReason={ordersFailure}
                onReload={() => setOrdersReload((key) => key + 1)}
                {...(onOpenContact ? { onOpenContact } : {})}
                scopedToOffer
                {...(membershipKind ? { empty: { title: t(`memberships.detail.emptyTitle.${membershipKind}`), body: t(`memberships.detail.emptyBody.${membershipKind}`) } } : {})}
              />
              )}
            </>
          ) : (
          <>
          {/*
            Ajustes: la misma revisión con la que termina el asistente.

            Y a propósito la misma —sus clases, sus renglones, su «Editar» a la
            derecha—: lo que se lee aquí es exactamente lo que allí se acaba de
            contestar, así que enseñarlo de otra forma obligaría a reconocer dos
            veces los mismos datos. Cada renglón abre el asistente por su paso;
            el que no se puede cambiar —el tipo— no lleva atajo.

            Esto sustituye a la tarjeta que vivía a la derecha. Allí competía por
            el ancho con la tabla de ventas y obligaba a leer los datos en una
            columna de trescientos píxeles; aquí tiene la pantalla entera y sólo
            se abre cuando se viene a configurar.
          */}
          <div className="dash-ofd-settings">
            {/* La tarjeta abre directamente con la lista: la portada, el nombre
                y el enlace se leen arriba, en la cabecera de la ficha, que está
                a la vista desde las tres pestañas. Repetirlos aquí obligaba a
                comprobar si las dos copias decían lo mismo. */}
            <OfferCard
              className="dash-ofd-primary-card"
              icon={<IconClipboard size={15} />}
              title={t('offers.detail.card.offer')}
              action={isBookable ? null : (
                <button type="button" className="dash-secondary-btn dash-summary-action" onClick={() => openWizard('info')}>
                  <IconSettings size={13} /> {t('offers.detail.editAll')}
                </button>
              )}
            >
            <div className={`dash-svc-review${isDigital ? ' is-digital' : ''}`}>
              {([
                /* El nombre encabeza la lista. Se lee además arriba, en la
                   cabecera de la ficha, y por eso no estaba: dos copias del
                   mismo dato obligan a comprobar si dicen lo mismo. Pero es el
                   primer dato de la oferta y quien viene a repasarla lo busca
                   aquí, con los demás — y la cabecera lo enseña como TÍTULO de
                   la pantalla, no como un dato que se pueda cambiar. */
                [t('offers.detail.fact.title'), offer.title, 'info', 'title', IconTag],
                [t('offers.new.shortDescriptionLabel'), offerShortDescription(offer) || t('offers.detail.noDescription'), 'info', 'shortDescription', IconAlignLeft],
                [t('offers.new.longDescriptionLabel'), offer.description.trim() || t('offers.detail.noDescription'), 'info', 'description', IconAlignLeft],
                [t('offers.detail.fact.price'), price ? price.formatted : t('offers.noPrice'), 'price', 'price', IconCurrencyDollar],
                [t('offers.detail.fact.billing'), price ? t(cadenceKeyOf(price)) : t('common.none'), 'price', 'billing', IconCreditCard],
                ...(isDigital
                  ? [[
                    t('offers.detail.fact.files'),
                    offer.digitalFiles.length > 0
                      ? t(offer.digitalFiles.length === 1 ? 'offers.detail.hero.fileOne' : 'offers.detail.hero.files', { count: offer.digitalFiles.length })
                      : t('common.none'),
                    'files',
                    'files',
                    IconFiles,
                  ] as const]
                  : []),
                /* Un producto físico: sus variantes —«Talla: S, M · Color:
                   Rojo»—, que se cambian en su paso del asistente. */
                ...(offer.type === 'physical'
                  ? [[
                    t('offers.new.step.variants'),
                    offer.variants?.length
                      ? (offer.variantOptions ?? []).map((option) => `${option.name}: ${option.values.join(', ')}`).join(' · ')
                      : t('offers.new.variants.none'),
                    'variants',
                    'variants',
                    IconStack2,
                  ] as const]
                  : []),
                /* Un producto físico: cuántos quedan —con variantes, todas
                   sumadas—. Cero se dice «Agotado», que es lo que ve el
                   comprador en el sitio. */
                ...(offer.type === 'physical'
                  ? [[
                    t('offers.detail.fact.stock'),
                    stockLeft === null
                      ? t('offers.new.shipping.unlimited')
                      : stockLeft === 0 ? t('offers.detail.soldOut') : t('offers.new.review.stockCount', { count: stockLeft }),
                    'shipping',
                    'stock',
                    IconTruckDelivery,
                  ] as const]
                  : []),
                [t('offers.detail.fact.after'), t(THANK_YOU_KEY[offer.thankYouMode]), 'delivery', 'after', IconCircleCheck],
                /* Lo propio de esta oferta —«Ingredientes», «Cómo usar»…—,
                   cada uno con su «Editar» al paso del asistente. */
                ...customRows,
                /* El tipo no está en la lista: no se cambia —lo eligió la puerta
                   por la que se creó— y ya se lee arriba, bajo «Ofertas». Una
                   fila que sólo se puede mirar, repitiendo lo que dice la
                   cabecera, es un renglón que se recorre para nada. */
              ] as const).map(([label, value, step, kind, Icon]) => (
                <div key={label} className={`is-${kind}`}>
                  <span><Icon size={14} aria-hidden="true" />{label}</span>
                  <strong title={value}>{value}</strong>
                  {step && !isBookable ? (
                    <button type="button" className="dash-secondary-btn dash-summary-action dash-ofd-row-edit" onClick={() => openWizard(step)}>
                      <IconPencil size={12} aria-hidden="true" />
                      <span>{t('offers.detail.edit')}</span>
                    </button>
                  ) : null}
                </div>
              ))}

              {/*
                Y si se ve o no, como una fila más.

                Estuvo debajo, en su propia caja y con un segmentado de dos
                pastillas: ocupaba el ancho entero de la tarjeta para una
                decisión que cabe donde caben el precio o la entrega. Aquí es un
                dato como los demás —rótulo a la izquierda, valor a la derecha—
                y el valor es el MISMO mando que la cabecera de arriba, no un
                tercer estilo de interruptor.

                Con agenda no: lo suyo se publica desde Reservas.
              */}
              {isBookable ? null : (
                <div className="is-visibility">
                  <span><IconEye size={14} aria-hidden="true" />{t('offers.detail.visibility')}</span>
                  <OfferStatusSelect
                    status={offer.status}
                    busy={savingField === 'status'}
                    blocked={publishBlock}
                    itemType={offer.type === 'digital' ? 'digital' : 'service'}
                    label={offer.title}
                    onSelect={(status) => { void patch({ status }, 'status') }}
                    onConnect={onOpenStripeSettings}
                    onOpenPlans={onOpenPlans}
                  />
                </div>
              )}
            </div>

            </OfferCard>

          {/* Reponer sin abrir el asistente. Ver `VariantStockCard`. */}
          {offer.type === 'physical' && offer.variants?.length
            ? <VariantStockCard projectId={projectId} token={token} offer={offer} onChange={onChange} />
            : null}

          {/* Etiquetas: para las colecciones automáticas. */}
          <OfferTagsCard projectId={projectId} token={token} offer={offer} onChange={onChange} {...(brands ? { brands } : {})} {...(onBrandRemoved ? { onBrandRemoved } : {})} {...(tags ? { tags } : {})} {...(onTagRemoved ? { onTagRemoved } : {})} />

          {/* Detalles técnicos */}
          <OfferCard className="dash-ofd-meta-card" icon={<IconClipboard size={15} />} title={t('offers.detail.card.meta')}>
            {/*
              Los datos técnicos, con las mismas filas que la tarjeta de arriba.

              Eran cuatro recuadros con su propio filo y su rótulo en versalitas
              —otra forma de decir «rótulo y valor» dentro de la misma pantalla—
              y quien bajaba hasta aquí tenía que reconocer una segunda manera de
              leer lo mismo. Con `dash-svc-review` son la misma fila: rótulo a la
              izquierda, valor a la derecha y, cuando la hay, su acción al final.
            */}
            <div className="dash-svc-review">
              <div className="is-id">
                <span><IconFingerprint size={14} aria-hidden="true" />{t('offers.detail.id')}</span>
                {/* Entero, con el completo también en el `title`: en media columna
                    se recorta con puntos, y lo que se hace con un identificador es
                    copiarlo — el botón de al lado se lleva el entero, no lo que se
                    ve. */}
                <strong><code title={offer.id}>{offer.id}</code></strong>
                <button
                  type="button"
                  className="dash-secondary-btn dash-summary-action dash-ofd-row-edit"
                  aria-label={t('offers.detail.copyIdAria')}
                  onClick={() => copyId()}
                >
                  <IconCopy size={12} aria-hidden="true" />
                  <span>{t('offers.detail.copy')}</span>
                </button>
              </div>
              {/* La dirección ya no está aquí: se fue a la pestaña «Página»,
                  junto al título de SEO y a la vista previa al compartir. Éste es
                  el sitio de lo que no se toca —el identificador y las fechas—, y
                  la dirección es justo lo contrario: es lo que se manda a la
                  gente, y cambiarla cambia el enlace que alguien ya copió. */}
              <div>
                <span><IconCalendarPlus size={14} aria-hidden="true" />{t('offers.detail.created')}</span>
                <strong>{created}</strong>
              </div>
              <div>
                <span><IconHistory size={14} aria-hidden="true" />{t('offers.detail.updated')}</span>
                <strong>{updated}</strong>
              </div>
            </div>
          </OfferCard>

          {/* 5. Zona de peligro */}
          <OfferCard icon={<IconX size={15} />} title={t('offers.detail.withdraw')} danger>
            <div className="dash-offer-side-state">
              <p>{t('offers.detail.withdrawBody')}</p>
              <button type="button" className="dash-danger-btn" onClick={() => setArchiveOpen(true)}>
                <IconX size={14} /> {t('offers.detail.withdraw')}
              </button>
            </div>
          </OfferCard>
          </div>
          </>
          )}
        </main>

          {/*
            Quién es esta oferta: a la derecha, en vertical y a la vista desde las
            tres pestañas.

            Estuvo encima de las pestañas, en horizontal. Ahí ocupaba un renglón
            entero del ancho de la pantalla para tres datos y tres botones, y sobre
            todo se iba de la vista en cuanto se bajaba a leer la tabla: para
            copiar el enlace o recordar de qué oferta se está hablando había que
            subir. En la columna de la derecha se queda quieta mientras se trabaja,
            que es cuando hace falta.

            Lo mínimo para saber qué se está mirando y hacer lo que se hace a
            diario: su portada, su nombre, si se ve o no, la descripción, la ficha
            en tres líneas y los tres mandos —copiar el enlace, abrirlo y entrar a
            cambiarla—. Todo lo demás vive dentro de su pestaña.
          */}
        {/* En «Apariencia» la tarjeta se oculta en pantallas anchas —ahí se mira
            una página entera— pero se conserva en el teléfono, donde va sobre las
            pestañas: si desapareciera, las pestañas subirían de sitio al cambiar
            de una a otra. La oculta el CSS, no el marcado. */}
        <section className={`dash-record-hero is-vertical${tab === 'apariencia' ? ' is-appearance-hidden' : ''}`} aria-label={offer.title}>
          <button
            type="button"
            className="dash-record-hero-cover"
            aria-label={t(offer.images[0] ? 'offers.detail.changeThumbAria' : 'offers.detail.addThumbAria')}
            disabled={savingField === 'images'}
            onClick={() => setImagePickerOpen(true)}
          >
            {savingField === 'images' ? (
              <IconLoader2 className="dash-spin" size={18} />
            ) : offer.images[0] ? (
              <>
                <img src={offer.images[0]} alt="" />
                {/* Se puede cambiar, y se dice al acercarse: la sombra sola
                    oscurecía la portada sin explicar por qué. */}
                <span className="dash-record-hero-cover-mark" aria-hidden="true">
                  <IconPhotoPlus size={17} />
                  <span>{t('common.changeImage')}</span>
                </span>
              </>
            ) : (
              /* Un solo bloque, no dos piezas cruzándose.
               *
               * El icono está desde el principio y no se mueve; lo único que
               * entra al acercarse es el rótulo. Con un icono de fondo
               * desvaneciéndose y otro del rótulo apareciendo, a mitad de
               * camino se veían los dos a medias — un parpadeo justo donde el
               * gesto tenía que sentirse limpio.
               *
               * `aria-hidden` porque el botón ya lo dice en su `aria-label`:
               * repetirlo haría que un lector de pantalla leyera dos veces lo
               * mismo al llegar aquí. */
              <span className="dash-record-hero-cover-mark is-empty" aria-hidden="true">
                <IconPhotoPlus size={18} />
                <span>{t('common.selectImage')}</span>
              </span>
            )}
          </button>
          {/* Las demás fotos, debajo de la portada. Ver `RecordPhotoStrip`. */}
          <RecordPhotoStrip
            projectId={projectId}
            token={token}
            images={offer.images}
            busy={savingField === 'images'}
            onChange={(images) => void patch({ images }, 'images')}
          />

          <div className="dash-record-hero-copy">
            {/* Qué es —«Digital product», «Booking»—: en el teléfono la cabecera
                de la página pierde su título y su subtítulo, y esto es lo que
                queda de ellos. En pantalla ancha lo dice la cabecera. */}
            <span className="dash-record-hero-kind">{isBookable ? t('offers.kind.booking') : t(TYPE_KEY[offer.type])}</span>
            <div className="dash-record-hero-titleline">
              <h2 title={offer.title}>{offer.title}</h2>
              {/* El estado, y pulsable: era una etiqueta que sólo se leía, y
                  publicar es justo lo que se viene a hacer después de dejar la
                  oferta lista. El mismo selector que la lista y que Reservas, así
                  que también explica por qué no se puede cuando no se puede. */}
              <OfferStatusSelect
                status={offer.status}
                busy={savingField === 'status'}
                blocked={publishBlock}
                itemType={offer.type === 'digital' ? 'digital' : 'service'}
                label={offer.title}
                onSelect={(status) => { void patch({ status }, 'status') }}
                onConnect={onOpenStripeSettings}
                onOpenPlans={onOpenPlans}
              />
            </div>
            {/* La descripción de la oferta, una línea y cortada, con la entera en
                el `title`: basta para reconocer cuál de tres ofertas parecidas es
                ésta, y se escribe en el asistente.

                Sin descripción no se pinta nada. Un «Sin descripción» en gris es
                un renglón que ocupa el mismo sitio que el texto que no hay, y de
                lo que informa es de que falta algo que no falta: una oferta sin
                descripción se vende igual. */}
            {offerShortDescription(offer) ? (
              <p title={offerShortDescription(offer)}>{offerShortDescription(offer)}</p>
            ) : null}

            {/*
              La ficha en una línea: cuánto cuesta, cómo se cobra y qué entrega.

              Son los tres datos que se consultan de pasada —«¿esto cuánto era?»— y
              estaban a dos clics, dentro de Ajustes. Aquí se leen sin abrir nada y
              no le quitan sitio a nada: ocupan el renglón que la portada ya gasta.

              Puntos y no comas: son datos sueltos del mismo objeto, no una
              enumeración. Cada uno se cambia en su paso del asistente, así que
              ninguno lleva atajo propio — para eso está «Ajustes» al lado.
            */}
            <ul className="dash-record-hero-meta">
              <li>{price ? price.formatted : t('offers.noPrice')}</li>
              {price ? <li>{t(cadenceKeyOf(price))}</li> : null}
              {isDigital && offer.digitalFiles.length > 0 ? (
                <li>{t(offer.digitalFiles.length === 1 ? 'offers.detail.hero.fileOne' : 'offers.detail.hero.files', { count: offer.digitalFiles.length })}</li>
              ) : null}
              {/* Un producto físico: cuántos quedan, que es lo que se viene a
                  mirar antes de reponer. Agotado en su color de aviso. */}
              {offer.type === 'physical' ? (
                <li className={stockLeft === 0 ? 'is-sold-out' : undefined}>
                  {stockLeft === null
                    ? t('offers.detail.hero.stockUnlimited')
                    : stockLeft === 0 ? t('offers.detail.soldOut') : t('offers.new.review.stockCount', { count: stockLeft })}
                </li>
              ) : null}
            </ul>
          </div>

          <div className="dash-record-hero-actions">
            {publicUrl ? (
              /*
                Copiar y abrir, en una sola pieza.
                Son dos formas de hacer lo mismo —llevarse el enlace— y como dos
                cajas sueltas se leían como dos decisiones. Pegados, con una raya
                entre ellos, son un mando con dos salidas; y al lado, separado,
                «Ajustes», que es lo único que cambia la oferta.
              */
              <div className="dash-record-hero-linkgroup">
                <button type="button" className="is-link-action" aria-label={t(copied ? 'offers.detail.link.copied' : 'offers.detail.link.copy')} onClick={() => { void copyLink() }}>
                  {/* Al copiar, el propio botón lo confirma durante un segundo. El
                      aviso de abajo lo dice también, pero llega al otro extremo de
                      la pantalla: la respuesta a un clic tiene que estar donde se
                      hizo el clic. */}
                  {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  <span>{t(copied ? 'offers.detail.link.copied' : 'offers.detail.link.copy')}</span>
                </button>
                {/* Abrir sólo si está publicada: en borrador el enlace contesta
                    «esto no está» y parecería que se rompió. */}
                {live ? (
                  <a href={publicUrl} target="_blank" rel="noreferrer" className="is-link-action" aria-label={t('offers.detail.link.open')}>
                    <IconExternalLink size={14} /><span>{t('offers.detail.link.open')}</span>
                  </a>
                ) : null}
              </div>
            ) : onOpenPublish ? (
              /* Sin sitio publicado no hay enlace todavía: el botón invita a
                 publicarlo y tiene el mismo estilo que el grupo de enlace para
                 mantener la armonía con «Ajustes». */
              <div className="dash-record-hero-linkgroup">
                <button
                  type="button"
                  onClick={() => { if (!blockUnsaved()) onOpenPublish() }}
                  title={t('offers.detail.link.needSite')}
                >
                  <IconWorld size={14} />
                  <span>{t('offers.detail.link.publishToGetLink')}</span>
                </button>
              </div>
            ) : (
              <small className="dash-ofd-need-site">{t('offers.detail.link.needSite')}</small>
            )}
            {/* «Ajustes» abre el ASISTENTE, no la pestaña: lo que se quiere al
                pulsarlo es cambiar la oferta, y la pestaña sólo la enseña. Con
                agenda no: lo suyo se edita en Reservas y arriba se dice dónde. */}
            {isBookable ? null : (
              <button type="button" className="dash-primary-btn" onClick={() => openWizard('info')}>
                <IconSettings size={15} /> {t('offers.detail.tab.settings')}
              </button>
            )}
          </div>
        </section>
      </div>

      {/* El asistente del alta, con la oferta dentro: los mismos pasos, las
          mismas validaciones y la misma vista previa que al crearla. Se abre por
          el paso que se haya pedido y devuelve la oferta guardada, que es lo que
          esta ficha vuelve a pintar. */}
      {membershipOpen ? (
        <MembershipEditor
          projectId={projectId}
          token={token}
          offerId={offer.id}
          onOpenPlans={onOpenPlans}
          onClose={() => setMembershipOpen(false)}
          onSaved={() => { void reloadAfterMembership() }}
        />
      ) : null}

      {editStep ? (
        <NewOfferWizard
          projectId={projectId}
          token={token}
          offer={offer}
          initialStep={editStep}
          canConnectStripe={canConnectStripe}
          plan={plan}
          publishedOffers={publishedOffers}
          {...(brands ? { brands } : {})}
          {...(onBrandRemoved ? { onBrandRemoved } : {})}
          {...(tags ? { tags } : {})}
          {...(onTagRemoved ? { onTagRemoved } : {})}
          onOpenPlans={onOpenPlans}
          /* Cerrarlo sin terminar vuelve a la lista: la ficha de algo «por
             completar» no tiene nada que leer todavía. */
          onClose={() => { setEditStep(null); if (offer.setupPending) onBack() }}
          onCreated={(saved) => { setEditStep(null); onChange(saved) }}
        />
      ) : null}

      {archiveOpen ? (
        <ConfirmDialog
          title={t('offers.withdraw.title')}
          message={t('offers.detail.withdrawBody')}
          confirmLabel={t('offers.withdraw.confirm')}
          tone="danger"
          onCancel={() => setArchiveOpen(false)}
          onConfirm={confirmArchive}
        />
      ) : null}

      {/* El cupo va en la ventana de plan: no se arregla conectando nada. */}
      {explainingPublish?.kind === 'limit' ? (
        <PlanLimitModal
          resource={t('usage.stat.offers').toLowerCase()}
          kind="offers"
          currentPlan={explainingPublish.plan}
          onClose={() => setExplainingPublish(null)}
          onOpenPlans={() => { setExplainingPublish(null); onOpenPlans() }}
        />
      ) : explainingPublish ? (
        <PublishBlockDialog
          kind={explainingPublish.kind}
          itemType={isBookable ? 'booking' : offer.type === 'digital' ? 'digital' : 'service'}
          connecting={false}
          onConnect={() => {
            setExplainingPublish(null)
            onOpenStripeSettings()
          }}
          onOpenPlans={() => {
            setExplainingPublish(null)
            onOpenPlans()
          }}
          onClose={() => setExplainingPublish(null)}
        />
      ) : null}

      {imagePickerOpen ? (
        <ImageLibraryPickerDialog
          token={token}
          projectId={projectId}
          onSelect={(url) => void patch(
            { images: [url, ...offer.images.slice(1).filter((img) => img !== url)].slice(0, 8) },
            'images',
          )}
          onClose={() => setImagePickerOpen(false)}
        />
      ) : null}
    </div>
  )
}
